/**
 * MainTaxonomyTab — the /main "Taxonomy" glossary.
 *
 * Auto-generated reference for every Main dashboard metric: name, definition,
 * source system and confidence, grouped by category. Read straight from the
 * signed-off metric definitions (.planning/audits/definitions.json via
 * loadDefinitions) so it can never drift from what the tiles actually compute.
 * A companion to the Summary readout — an exec can look up exactly what any
 * number means and where it comes from.
 */
import { useMemo, useState } from 'react';

import { loadDefinitions, type Definition } from '../../lib/definitions.js';
import { Card, CardContent } from '../ui/Card.js';

/** Companion/data-only ids that back another tile and have no standalone card. */
const NON_TILE_IDS = new Set<string>(['MAIN-48']);

/** Preferred display order for categories; anything else sorts after, A–Z. */
const CATEGORY_ORDER = ['Activity', 'Pipeline', 'Pipeline Efficiency', 'Marketing', 'ROI'];

const CONFIDENCE_CLASS: Record<Definition['confidence'], string> = {
  high: 'bg-good-bg text-good-text',
  medium: 'bg-warn-bg text-warn-text',
  low: 'bg-ink-100 text-ink-500',
};

/** MAIN-* metrics that map to a real, non-deferred tile, in definition order. */
function mainMetrics(): Definition[] {
  return loadDefinitions().metrics.filter(
    (m) =>
      m.metric_id.startsWith('MAIN-') &&
      m.status === undefined && // exclude dropped / deferred-to-phase-6
      !NON_TILE_IDS.has(m.metric_id),
  );
}

export function MainTaxonomyTab(): React.JSX.Element {
  const [query, setQuery] = useState('');

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = mainMetrics().filter((m) => {
      if (q === '') return true;
      return (
        m.name.toLowerCase().includes(q) ||
        m.metric_id.toLowerCase().includes(q) ||
        m.formula_plain_english.toLowerCase().includes(q) ||
        m.category.toLowerCase().includes(q)
      );
    });

    const byCategory = new Map<string, Definition[]>();
    for (const m of filtered) {
      const list = byCategory.get(m.category) ?? [];
      list.push(m);
      byCategory.set(m.category, list);
    }

    return [...byCategory.entries()].sort(([a], [b]) => {
      const ia = CATEGORY_ORDER.indexOf(a);
      const ib = CATEGORY_ORDER.indexOf(b);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.localeCompare(b);
    });
  }, [query]);

  const total = groups.reduce((n, [, list]) => n + list.length, 0);

  return (
    <div className="space-y-6">
      <Card className="border-ink-100 bg-ink-50/40">
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <h1 className="text-lg font-semibold text-ink-900">Metric taxonomy</h1>
            <p className="max-w-3xl text-sm text-ink-600">
              What every number on this dashboard means and where it comes from. Definitions are
              the signed-off source of truth — the same text behind each tile’s “?” tooltip.
            </p>
          </div>
          <input
            type="search"
            value={query}
            onChange={(e) => { setQuery(e.target.value); }}
            placeholder="Search metrics — name, id, or wording…"
            aria-label="Search metrics"
            className="w-full max-w-md rounded-lg border border-ink-200 px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </CardContent>
      </Card>

      {total === 0 && (
        <p className="text-sm italic text-ink-400" data-testid="taxonomy-empty">
          No metrics match “{query}”.
        </p>
      )}

      {groups.map(([category, metrics]) => (
        <section key={category} aria-labelledby={`taxo-${category}`}>
          <h2
            id={`taxo-${category}`}
            className="section-label mb-3 shrink-0"
          >
            {category} ({metrics.length})
          </h2>
          <div className="space-y-3">
            {metrics.map((m) => (
              <Card key={m.metric_id} data-testid={`taxonomy-${m.metric_id}`}>
                <CardContent className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-semibold text-ink-900">{m.name}</h3>
                    <span className="rounded bg-ink-100 px-1.5 py-0.5 font-mono text-[11px] text-ink-500">
                      {m.metric_id}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${CONFIDENCE_CLASS[m.confidence]}`}
                    >
                      {m.confidence} confidence
                    </span>
                  </div>

                  <p className="text-xs text-ink-400">
                    Source: {m.source_of_truth_system}
                  </p>

                  <details className="group">
                    <summary className="cursor-pointer select-none text-sm font-medium text-brand-600 hover:text-brand-700">
                      How it’s calculated
                    </summary>
                    <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-ink-700">
                      {m.formula_plain_english}
                    </p>
                    {m.salesforce_fields_used.length > 0 && (
                      <p className="mt-2 text-xs text-ink-400">
                        <span className="font-semibold text-ink-500">Salesforce fields: </span>
                        {m.salesforce_fields_used.join(', ')}
                      </p>
                    )}
                  </details>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
